import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  AddressType,
  GooglePlacesAddress,
  Region,
  useUserLocation,
  ViewportBounds,
} from '@sweetgreen/garnish';
import {
  convertGooglePlacesAddress,
  findRegionBounds,
  useNoticeBannersStackContext,
  useResponsive,
} from '@sweetgreen/garnish';

import type { Location } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';
import { getEnvVars } from '@order/utils';

import type { LocationResultData } from '../GraphQL';
import {
  useCenteredLocationsByBoundingBox,
  useCenteredLocationsByString,
  useLazyLocationByAddress,
} from '../GraphQL';
import { useSearchLocationsByBoundingBoxQuery } from '../GraphQL/LocationScreen.searchLocationsByBoundingBox.generated';
import { useSearchLocationsByStringQuery } from '../GraphQL/LocationScreen.searchLocationsByString.generated';
import type { LocationQueryType } from '../LocationScreen.types';
import {
  DEFAULT_BOUNDS,
  getLocationsBySearchType,
  getUserDistance,
  parseAddressIntoInput,
} from '../LocationScreen.utils';

/**
 * Returns locations based on locations search field or map region.
 */
export const useLocationSearch = (props: UseLocationSearchProps) => {
  const {
    searchString,
    locationSearchType,
    userLocation,
    setRegion,
    getUserGeolocation,
  } = props;

  // ─────────────────────────────────────────────────────────────────

  const [locationQueryType, setLocationQueryType] =
    useState<LocationQueryType>('');

  const {
    updateViewportBounds,
    searchViewportBounds,
    updateSearchViewportBounds,
  } = useViewPortBounds();

  // ─── QUERIES ─────────────────────────────────────────────────────

  const hasNotEnteredMinimalRequiredChars = searchString.trim().length < 3;
  const isSearchByStringType = locationQueryType === 'searchString';
  const shouldNotQueryLocationsByString =
    !isSearchByStringType ||
    (isSearchByStringType && hasNotEnteredMinimalRequiredChars);
  const shouldNotQueryLocationsByBoundingBox = locationQueryType !== 'region';

  const [locationsByString] = useSearchLocationsByStringQuery({
    variables: { searchString },
    requestPolicy: 'cache-and-network',
    pause: shouldNotQueryLocationsByString,
  });
  const [locationsByBoundingBox] = useSearchLocationsByBoundingBoxQuery({
    variables: searchViewportBounds,
    requestPolicy: 'cache-and-network',
    pause: shouldNotQueryLocationsByBoundingBox,
  });

  // ─────────────────────────────────────────────────────────────────

  const centerLocationsByString =
    useCenteredLocationsByString(locationsByString);
  const centerLocationsByBoundingBox = useCenteredLocationsByBoundingBox(
    locationsByBoundingBox,
  );

  // ─── Location Results ─────────────────────────────────────────────────────
  const locationsResponse =
    locationQueryType === 'searchString'
      ? centerLocationsByString
      : centerLocationsByBoundingBox;

  // Destructuring
  const { center, stale, fetching, locations, ...restResults } =
    locationsResponse;
  const { lat, lng, zoom } = center ?? {};
  const isFetching = fetching || stale;

  // Filtering locations by search type, then adding and sorting by distance.
  const locationsForSelectedSearchType = useFilteredLocationResults({
    locationResults: locations,
    locationSearchType,
    userLocation,
  });
  const locationsForOppositeSearchType = useFilteredLocationResults({
    locationResults: locations,
    locationSearchType: locationSearchType === 'pickup' ? 'outpost' : 'pickup',
    userLocation,
  });

  // Locations array should be sorted by search type.
  const sortedLocations = useMemo(
    () => [
      ...locationsForSelectedSearchType,
      ...locationsForOppositeSearchType,
    ],
    [locationsForSelectedSearchType, locationsForOppositeSearchType],
  );

  // ─── HELPERS ─────────────────────────────────────────────────────

  const { t } = useLocalizationContext();
  const { currentBreakpoint } = useResponsive();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  const showGeolocationPermissionWarning = useCallback(() => {
    const enablePermissionText = currentBreakpoint.isMobile
      ? t('location.enable-location-services-mobile')
      : t('location.enable-location-services');

    addNoticeBanner(
      {
        id: 'location-permission-warning',
        text: enablePermissionText,
        palette: 'neutral',
      },
      true,
    );
  }, [currentBreakpoint.isMobile, addNoticeBanner, t]);

  const fetchLocationsBasedOnUserGeolocation = useCallback(
    async (options?: FetchLocationsBasedOnUserGeolocationOptions) => {
      const { showWarningIfGeolocationDisabled = true } = options ?? {};

      const userGeoLocation = await getUserGeolocation();
      const { coords } = userGeoLocation ?? {};
      const { latitude, longitude } = coords ?? {};

      // ─────────────────────────────────────────────────────────────────

      if (!userGeoLocation && showWarningIfGeolocationDisabled) {
        showGeolocationPermissionWarning();
      }

      if (userGeoLocation) {
        const userGeolocationViewportBounds = findRegionBounds({
          latitude,
          longitude,
        });

        updateSearchViewportBounds(userGeolocationViewportBounds);
        setLocationQueryType('region');
      }

      // ─────────────────────────────────────────────────────────────────

      return userGeoLocation;
    },
    [
      getUserGeolocation,
      showGeolocationPermissionWarning,
      updateSearchViewportBounds,
    ],
  );

  // reference version to prevent re-renders
  const { current: fetchLocationsBasedOnUserGeolocationMemoized } = useRef(
    fetchLocationsBasedOnUserGeolocation,
  );

  // ─── EFFECTS ─────────────────────────────────────────────────────

  // Update region with locations results and on location search type change
  useEffect(() => {
    if (!lat || !lng || !zoom) return;

    setRegion({ lat, lng, zoom });
  }, [lat, lng, zoom, locationSearchType, setRegion]);

  // ─────────────────────────────────────────────────────────────────

  return {
    ...restResults,
    locations: sortedLocations,
    locationsForSelectedSearchType,
    locationsForOppositeSearchType,
    hasResults: locations.length > 0,
    isFetching,
    locationQueryType,
    lat,
    lng,
    zoom,
    setLocationQueryType,
    updateViewportBounds,
    updateSearchViewportBounds,
    fetchLocationsBasedOnUserGeolocation:
      fetchLocationsBasedOnUserGeolocationMemoized,
    locationsByBoundingBox,
  };
};

/**
 * 1. Excludes outpost results from pickup search and otherwise.
 * 2. Adds the distance to the user if the geolocation is available.
 * 3. Sorts the results by distance to the user (closest first).
 */
export const useFilteredLocationResults = ({
  locationResults,
  locationSearchType,
  userLocation,
}: UseFilteredLocationsProps) => {
  return useMemo(() => {
    return getLocationsBySearchType(locationSearchType, locationResults)
      .map((location) => ({
        ...location,
        distance: getUserDistance(userLocation?.geolocation, location),
      }))
      .sort((first, second) => (first.distance ?? 0) - (second.distance ?? 0));
  }, [locationSearchType, locationResults, userLocation?.geolocation]);
};

export const useCallbacks = ({
  setLocationQueryType,
  setSearchString,
  setSelectedLocation,
  toggleSearchAreaBtnBasedOnRegion,
  updateViewportBounds,
  updateSearchViewportBounds,
}: {
  readonly setLocationQueryType: (queryType: LocationQueryType) => void;
  readonly setSearchString: (input: string) => void;
  readonly setSelectedLocation: (location: string) => void;
  readonly toggleSearchAreaBtnBasedOnRegion: () => void;
  readonly updateViewportBounds: (region: Region) => void;
  readonly updateSearchViewportBounds: () => void;
}) => {
  const onSearchInputSubmit = React.useCallback(
    (searchStr: string) => {
      setLocationQueryType('searchString');
      setSearchString(searchStr ?? '');
    },
    [setLocationQueryType, setSearchString],
  );

  const onSearchAreaBtnPress = React.useCallback(() => {
    setLocationQueryType('region');
    setSearchString('');
    updateSearchViewportBounds();
  }, [setLocationQueryType, setSearchString, updateSearchViewportBounds]);

  const onRegionChange = React.useCallback(
    (newRegion: Region) => {
      toggleSearchAreaBtnBasedOnRegion();
      updateViewportBounds(newRegion);
    },
    [toggleSearchAreaBtnBasedOnRegion, updateViewportBounds],
  );

  const onMapPress = React.useCallback(() => {
    setSelectedLocation('');
  }, [setSelectedLocation]);

  return {
    onSearchInputSubmit,
    onSearchAreaBtnPress,
    onRegionChange,
    onMapPress,
  };
};

export const useLocationsByAddress = (placeId: string) => {
  const [loadingAddress, setLoadingAddress] = React.useState(false);
  const [googlePlacesAddress, setGooglePlacesAddress] = React.useState<
    Partial<GooglePlacesAddress>
  >({});
  const [results, executeQuery] = useLazyLocationByAddress({
    input: parseAddressIntoInput(googlePlacesAddress),
  });

  React.useEffect(() => {
    if (!placeId) return;
    let isMounted = true;

    (async () => {
      setLoadingAddress(true);
      const response = await convertGooglePlacesAddress({
        placeId,
        apiKey: getEnvVars().GOOGLE_API_KEY,
      });

      if (!isMounted) return;
      setLoadingAddress(false);
      setGooglePlacesAddress(response);
      executeQuery();
    })();

    return () => {
      isMounted = false;
    };
  }, [placeId, executeQuery]);
  const locationData = results.data?.locationByAddress;
  const hasError = Boolean(results.error);
  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const errorText = t('general.error');

  React.useEffect(() => {
    if (!hasError) return;
    addNoticeBanner({ text: errorText, palette: 'caution' });
  }, [hasError, errorText, addNoticeBanner]);

  return {
    googlePlacesAddress,
    fetchingDelivery: loadingAddress || results.fetching,
    foundLocation: placeId ? (locationData as Location) : undefined,
    noLocationFound: placeId
      ? locationData?.__typename === 'NoValidRestaurants'
      : false,
    setLoadingAddress,
    setGooglePlacesAddress,
  };
};

const useViewPortBounds = () => {
  // current viewport boundaries, that change while the user interacts with map.
  const [viewportBounds, setViewportBounds] =
    React.useState<ViewportBounds>(DEFAULT_BOUNDS);

  // search region viewport bounds that change in response to corresponding actions such as "Search area" or a change in the user's geolocation.
  const [searchViewportBounds, setSearchViewportBounds] =
    React.useState<ViewportBounds>(DEFAULT_BOUNDS);

  // ─── HELPERS ─────────────────────────────────────────────────────

  const updateViewportBounds = React.useCallback(
    (newRegion: Region) => {
      const { topLeft, bottomRight } = newRegion;

      if (topLeft === undefined || bottomRight === undefined) return;

      setViewportBounds({ topLeft, bottomRight });
    },
    [setViewportBounds],
  );

  const updateSearchViewportBounds = React.useCallback(
    (newViewportBounds = viewportBounds) => {
      setSearchViewportBounds(newViewportBounds);
    },
    [viewportBounds],
  );

  // ─────────────────────────────────────────────────────────────────

  return {
    viewportBounds,
    updateViewportBounds,
    searchViewportBounds,
    updateSearchViewportBounds,
  };
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type UseLocationSearchProps = {
  readonly searchString: string;
  readonly locationSearchType: AddressType;
  readonly userLocation: ReturnType<typeof useUserLocation>;
  readonly setRegion: (region: Region) => void;
  readonly getUserGeolocation: ReturnType<
    typeof useUserLocation
  >['getUserGeolocation'];
};

type UseFilteredLocationsProps = {
  readonly locationResults: readonly LocationResultData[];
  readonly locationSearchType: AddressType;
  readonly userLocation?: ReturnType<typeof useUserLocation>;
};

type FetchLocationsBasedOnUserGeolocationOptions = {
  readonly showWarningIfGeolocationDisabled: boolean;
};
